// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import CustomSnackbar from '../ui/Snackbars';
import dayjs from 'dayjs';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from "@material-ui/core/TableHead";
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import wso2Theme from '../../theme';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { currencyTypes, travelStatus } from '../../utils/const';
import { getDateForDB, dayDifferent } from '../../utils/formatting';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getPerdiemAllocations, getPerdiemMetaData, postPerdiemAllocations, deletePerdiemAllocation } from '../../utils/end-points';
import { isAccessGranted } from "../../utils/oauth";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MenuItem, Select } from '@mui/material';
import { PRIVILEGES } from "../../Config.js";
import "../../css/PerdiemInformation.scss"

const headCells = [
    { id: 'start_date', label: 'Start Date/Time', align: "left", disablePadding: false, },
    { id: 'end_date', label: 'End Date/Time', align: "left", disablePadding: false },
    { id: 'no_of_days', label: 'No. of Days', align: "left", disablePadding: false },
    { id: 'currency', label: 'Currency', align: "left", disablePadding: false },
    { id: 'perdiem_value', label: 'Per Diem Amount', align: "left", disablePadding: false },
    { id: 'total', label: 'Total Amount', align: "left", disablePadding: false },
    { id: 'action', label: 'Action', align: "left", disablePadding: false }
];

function EnhancedTableHead(props) {
    const { order, orderBy } = props;

    return (
        <TableHead>
            <TableRow >
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.align}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        className='Head-label' >
                        {headCell.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const PerdiemInformation = (props) => {

    const { travel, fetchPerdiemPaymentSummary, isLoading, setIsLoading} = props
    const { handleRequest } = useHttp()
    const [perdiemMetaData, setPerdiemMetaData] = useState(true)
    const [perdiems, setPerdiems] = useState([])
    const [isSaveEnable, setSaveEnable] = useState(false)
    const [isResetEnable, setResetEnable] = useState(false)
    const [editingRowId, setEditingRowId] = useState(null)
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })

    useEffect(() => {
        fetchPerdiemData(false)
    }, [])

    const fetchPerdiemData = useCallback(async (showSuccessSnack) => {
        setIsLoading(true)
        setEditingRowId(null)
        setResetEnable(false)
        setSaveEnable(false)
        const promises = []

        if (isAccessGranted([PRIVILEGES.ADD_PERDIEMS])){
            promises.push(new Promise(function (resolve, reject) {
                handleRequest(getPerdiemMetaData + "?status=active", "GET", null, (data) => {
                    if (data) {
                        setPerdiemMetaData(data)
                        resolve()
                    }
                    else {
                        setSnackbarData({ message: "Error while fetching perdiem information", open: true, severity: "error", onClose: onCloseSnackbar })
                        reject()
                    }
                }, (error) => {
                    setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                    reject()
                })
            }))
        }

        promises.push(new Promise(function (resolve, reject) {
            handleRequest(getPerdiemAllocations.replace("$jobNumber", travel.jobNumber) + "?status=active", "GET", null, (perdiemAllocations) => {
                if (perdiemAllocations) {
                    let sortedPerdiemAllocations = perdiemAllocations.filter(perdiem => perdiem.startDate === null)
                    let tempPerdiemAllocations = perdiemAllocations.filter(perdiem => perdiem.startDate !== null).sort((a, b) => (a.startDate > b.startDate)
                        ? 1
                        : ((b.startDate > a.startDate)
                            ? -1
                            : 0))
                    sortedPerdiemAllocations = sortedPerdiemAllocations.concat(tempPerdiemAllocations)
                    setPerdiems(sortedPerdiemAllocations)
                    resolve()
                }
                else {
                    setSnackbarData({ message: "Error while fetching perdiem allocations", open: true, severity: "error", onClose: onCloseSnackbar })
                    reject()
                }
            }, (error) => {
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                reject()
            })
        }))

        Promise.all(promises)
            .then(() => {
                if (showSuccessSnack) {
                    setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })
                }
            })
            .catch((error) => {
                setSnackbarData({ message: "Failed", open: true, severity: "error", onClose: onCloseSnackbar })
                console.error(error)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    // adding new perdiem by validating existing perdiem
    const validateAndAddPerdiem = () => {
        setSaveEnable(false)
        // validating last added perdiem information
        const newPerdiems = perdiems.filter(perdiem => perdiem.isNewPerdiem === true)
        if (newPerdiems.length > 0) {
            let lastAddedPerdiem = newPerdiems[newPerdiems.length - 1]
            lastAddedPerdiem = validatePerdiemInformation(lastAddedPerdiem, true)
            if (!lastAddedPerdiem.isError) {
                setPerdiems([...perdiems, createNewPerdiem()])
            }
            else {
                setSaveEnable(false)
            }
        }
        else {
            let perdiemObj = createNewPerdiem()
            setPerdiems([...perdiems, perdiemObj])
            if (!validatePerdiemInformation(perdiemObj).isError) {
                setSaveEnable(true)
            }
        }
    }

    // creating new perdiem object
    const createNewPerdiem = () => {
        setEditingRowId(perdiems.length)

        var tempStartDate = perdiems.length > 0 && perdiems[perdiems.length - 1].endDate
            ? new Date(perdiems[perdiems.length - 1].endDate)
            : new Date(travel.startDate)
        var tempEndDate = new Date(travel.endDate)

        // filtering perdiem metadata information related to the travel request's country
        var filteredPerdiemMetadata = perdiemMetaData.filter(data => data.country === travel.country)
        // creating new perdiem object
        const perdiemObj = {
            startDate: tempStartDate,
            endDate: tempEndDate,
            noOfDays: dayDifferent(new Date(getDateForDB(tempEndDate)), new Date(getDateForDB(tempStartDate))) + 1,
            currency: filteredPerdiemMetadata.length > 0 ? filteredPerdiemMetadata[0].currency : null,
            perDiemValue: filteredPerdiemMetadata.length > 0 ? parseFloat(filteredPerdiemMetadata[0].amount) : 0,
            engagementCode: travel.engagementCode,
            isNewPerdiem: true,
            isError: false
        }
        perdiemObj.total = calculateTotalPerdiemAmount(perdiemObj)

        setResetEnable(true)
        return perdiemObj
    }

    // function to validate information of a perdiem object
    const validatePerdiemInformation = (perdiemObj, showError) => {
        perdiemObj.isError = false
        let errorMessage = "Please re-check the information. "

        if (!perdiemObj.startDate || !perdiemObj.endDate || !perdiemObj.currency || !perdiemObj.perDiemValue) {
            let errorCount = 0
            perdiemObj.isError = true

            if (showError) {
                if (!perdiemObj.startDate) { errorMessage = errorMessage.concat("Start Date/Time, "); errorCount += 1; }
                if (!perdiemObj.endDate) { errorMessage = errorMessage.concat("End Date/Time, "); errorCount += 1; }
                if (!perdiemObj.currency) { errorMessage = errorMessage.concat("Currency, "); errorCount += 1; }
                if (!perdiemObj.perDiemValue) { errorMessage = errorMessage.concat("Perdiem Amount, "); errorCount += 1; }
                errorMessage = errorMessage.slice(0, -2);
                if (errorCount > 1) {
                    errorMessage = errorMessage.concat(" are mandatory.")
                }
                else {
                    errorMessage = errorMessage.concat(" is mandatory.")
                }
                setSnackbarData({ message: errorMessage, open: true, severity: "error", onClose: onCloseSnackbar })
            }
            else {
                setSnackbarData({ message: "", open: false, severity: "error", onClose: onCloseSnackbar })
            }

        }

        // Validate currency consistency
        if (perdiems.length > 0 && perdiemObj.currency !== perdiems[0].currency) {
            perdiemObj.isError = true;
            setSnackbarData({
                message: "Currency mismatch! All per diems in a travel request must use the same currency.",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar
            });
        }

        if (!perdiemObj.isError) {
            perdiemObj = validatePerdiemDatesAndTimes(perdiemObj)
        }
        return perdiemObj
    }

    // function to validate dates of a perdiem object
    const validatePerdiemDatesAndTimes = (perdiemObj) => {
        if (perdiemObj.endDate <= perdiemObj.startDate || !(perdiemObj.noOfDays > 0)) {
            setSnackbarData({ message: "Please check the dates again", open: true, severity: "error", onClose: onCloseSnackbar })
            perdiemObj.isError = true
        }
        // validate perdiem dates are within travel dates
        // if (perdiemObj.endDate > new Date(travel.endDate) || perdiemObj.startDate < new Date(travel.startDate)) {
        //     setSnackbarData({ message: "Perdiem dates should be within travelling dates range", open: true, severity: "error", onClose: onCloseSnackbar })
        //     perdiemObj.isError = true
        // }
        return perdiemObj
    }

    const getEditField = (id, fieldName) => {
        switch (fieldName) {

            case "startDate":
                return <LocalizationProvider dateAdapter={AdapterDayjs}>

                    <DateTimePicker
                        renderInput={(props) => <TextField {...props} size="small" error={perdiems[id].isDateError ? perdiems[id].isDateError : false} />}
                        value={perdiems[id]?.startDate}
                        onChange={(value) => setEditFieldValue(value.$d, id, fieldName)}
                        // minDate={dayjs(travel.startDate)}
                        // maxDate={dayjs(travel.endDate)}
                        inputFormat="YYYY-MM-DD HH:mm"
                    />
                </LocalizationProvider>

            case "endDate":

                return <LocalizationProvider dateAdapter={AdapterDayjs}>

                    <DateTimePicker
                        renderInput={(props) => <TextField {...props} size="small" error={perdiems[id].isDateError ? perdiems[id].isDateError : false} />}
                        value={perdiems[id]?.endDate}
                        onChange={(value) => setEditFieldValue(value.$d, id, fieldName)}
                        // minDate={dayjs(travel.startDate)}
                        // maxDate={dayjs(travel.endDate)}
                        inputFormat="YYYY-MM-DD HH:mm"
                    />
                </LocalizationProvider>

            case "currency":
                return <Select value={perdiems[id][fieldName]} fullWidth
                    onChange={(event) => setEditFieldValue(event.target.value, id, fieldName)} size='small'>
                    {currencyTypes.length > 0 && currencyTypes.map(currency => {
                        return <MenuItem key={currency[0]} value={currency[0]}>
                            <span>{currency[0] + " (" + currency[1] + ")"}</span>
                        </MenuItem>
                    })}
                </Select>

            case "noOfDays":
                return <TextField variant="outlined" size='small' disabled
                    value={perdiems[id][fieldName] ? perdiems[id][fieldName] : ""}
                    onChange={(event) => setEditFieldValue(event.target.value, id, fieldName)} />

            case "perDiemValue":
                return <TextField variant="outlined" size='small' type='number'
                    value={perdiems[id][fieldName] ? perdiems[id][fieldName] : ""}
                    onChange={(event) => setEditFieldValue(parseFloat(event.target.value), id, fieldName)} />

            case "total":
                return <TextField variant="outlined" size='small' type='number' disabled
                    value={perdiems[id][fieldName] ? perdiems[id][fieldName] : ""}
                    onChange={(event) => setEditFieldValue(event.target.value, id, fieldName)} />

            default:
                return <TextField variant="outlined" size='small'
                    value={perdiems[id][fieldName] ? perdiems[id][fieldName] : ""}
                    onChange={(event) => setEditFieldValue(event.target.value, id, fieldName)} />
        }
    }

    const setEditFieldValue = (value, id, fieldName) => {
        let perdiemObj = perdiems[id]
        perdiemObj[fieldName] = value

        perdiemObj.noOfDays = dayDifferent(new Date(getDateForDB(perdiemObj.endDate)), new Date(getDateForDB(perdiemObj.startDate))) + 1
        perdiemObj.total = calculateTotalPerdiemAmount(perdiemObj)

        perdiemObj = validatePerdiemInformation(perdiemObj, false)
        if (perdiemObj.isError) {
            setSaveEnable(false)
        }
        else {
            let perdiemArr = [...perdiems]
            perdiemArr[id] = perdiemObj
            setPerdiems(perdiemArr)
            setSaveEnable(true)
        }
    }

    const calculateTotalPerdiemAmount = (perdiemObj) => {
        let total = 0
        if (moment(perdiemObj.startDate).format('HH:mm') <= "12:00") {
            total = total + +perdiemObj.perDiemValue
        }
        else {
            total = total + 0.5 * +perdiemObj.perDiemValue
        }
        if (moment(perdiemObj.endDate).format('HH:mm') >= "12:00") {
            total = total + +perdiemObj.perDiemValue
        }
        else {
            total = total + 0.5 * +perdiemObj.perDiemValue
        }
        total = total + (perdiemObj.noOfDays - 2) * +perdiemObj.perDiemValue
        return total
    }

    const resetPerdiems = () => {

        setConfirmationDialogData({
            open: true,
            title: "Reset Perdiems",
            content: "Are you sure you want reset perdiems into last saved values?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: handleAcceptResetAlert,
            declineAlert: handleDeclineAlert
        })
    }

    const savePerdiems = () => {

        setConfirmationDialogData({
            open: true,
            title: "Save Perdiems",
            content: "Are you sure you want to save these perdiems?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: handleAcceptSaveAlert,
            declineAlert: handleDeclineAlert
        })
    }

    const isDeleteDisabled = () => {
        return (
          !isAccessGranted([PRIVILEGES.DELETE_PERDIEMS]) ||
          !(travel.status === travelStatus.INPROGRESS || travel.status === travelStatus.EXTENDED)
        );
      };

    const deletePerdiems = (perdiemObj, key) => {
        setConfirmationDialogData({
            open: true,
            title: "Delete Perdiem",
            content: "Are you sure you want to delete this perdiem?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: () => handleAcceptDeleteAlert(perdiemObj, key),
            declineAlert: handleDeclineAlert
        })
    }

    const handleAcceptResetAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
        fetchPerdiemData(false)
    }

    const handleAcceptSaveAlert = useCallback(async () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
        setIsLoading(true)
        const newPerdiemAllocations = perdiems.filter(perdiem => perdiem.isNewPerdiem === true)
        newPerdiemAllocations.map(perdiem => {
            perdiem.startDate = moment(perdiem.startDate).format('YYYY-MM-DD HH:mm:ss')
            perdiem.endDate = moment(perdiem.endDate).format('YYYY-MM-DD HH:mm:ss')
        })

        handleRequest(postPerdiemAllocations.replace("$jobNumber", travel.jobNumber), "POST", newPerdiemAllocations, (response) => {
            if (response) {
                fetchPerdiemData(true)
                fetchPerdiemPaymentSummary();
            }
            else {
                setIsLoading(false)
                setSnackbarData({ message: "Error while inserting perdiem allocations", open: true, severity: "error", onClose: onCloseSnackbar })
            }
        }, (error) => {
            setIsLoading(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    })

  const handleAcceptDeleteAlert = useCallback(async (perdiemObj, id) => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    if (perdiemObj?.isNewPerdiem && perdiemObj.isNewPerdiem) {
      let perdiemAllocations = perdiems;
      perdiemAllocations = perdiemAllocations.filter(
        (perdiem, key) => key !== id
      );
      setPerdiems(perdiemAllocations);
    } else {
      setIsLoading(true);
      handleRequest(
        deletePerdiemAllocation.replace("$id", perdiemObj.id),
        "DELETE",
        null,
        (response) => {
          if (response) {
            let perdiemAllocations = perdiems;
            perdiemAllocations = perdiemAllocations.filter(
              (perdiem, key) => key !== id
            );
            setPerdiems(perdiemAllocations);
            setEditingRowId(editingRowId - 1);
            fetchPerdiemPaymentSummary();
            setSnackbarData({
              message: "Success",
              open: true,
              severity: "success",
              onClose: onCloseSnackbar,
            });
          } else {
            setSnackbarData({
              message: "Error while deleting perdiem allocation",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
          setIsLoading(false);
        },
        (error) => {
          setIsLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    }
  });

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }


    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <div className='Perdiem-information'>
            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
            <CustomSnackbar snackbar={snackbarData} />

            <div>
                <TableContainer className="Table-container">

                    <Table size={'small'}>
                        <EnhancedTableHead/>
                        <TableBody >
                            {perdiems.map((row, key) => {
                                return (
                                    <TableRow hover tabIndex={-1} key={key}>
                                        <TableCell className='Table-cell Table-cell-date-time'>
                                            <div className='Date-cell'>{key === editingRowId ? getEditField(key, "startDate") : row.startDate ? moment(row.startDate).format('YYYY-MM-DD HH:mm') : "n.d."}</div>
                                        </TableCell>
                                        <TableCell className='Table-cell Table-cell-date-time'>
                                            <div className='Date-cell'>{key === editingRowId ? getEditField(key, "endDate") : row.endDate ? moment(row.endDate).format('YYYY-MM-DD HH:mm') : "n.d."}</div>
                                        </TableCell>
                                        <TableCell className='Table-cell Table-cell-no-of-days'>{key === editingRowId ? getEditField(key, "noOfDays") : row.noOfDays}</TableCell>
                                        <TableCell className='Table-cell Table-cell-currency'>{key === editingRowId ? getEditField(key, "currency") : row.currency}</TableCell>
                                        <TableCell className='Table-cell Table-cell-cost'>{key === editingRowId ? getEditField(key, "perDiemValue") : row.perDiemValue}</TableCell>
                                        <TableCell className='Table-cell Table-cell-cost'>{key === editingRowId ? getEditField(key, "total") : row.total}</TableCell>
                                        <TableCell className='Table-cell Table-cell-action'>
                                            <DeleteForeverIcon 
                                                color='secondary' 
                                                className='Remove-icon' 
                                                onClick={() => {
                                                    if (!isDeleteDisabled()) {
                                                      deletePerdiems(row, key);
                                                    }
                                                  }}
                                                disabled={isDeleteDisabled(travel)}
                                                style={{
                                                    color: isDeleteDisabled(travel)
                                                        ? wso2Theme.palette.disabled.main
                                                        : wso2Theme.palette.secondary.main,
                                                        cursor: isDeleteDisabled(row) ? "default" : "pointer",
                                                        ...(isDeleteDisabled(travel) && { pointerEvents: "none" })
                                                }} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {!isLoading && perdiems.length === 0 && <div className='No-perdiems-label'>No Perdiems</div>}
                </TableContainer>
                <Grid container className='Button-grid'>
                    <Grid item lg={3} className='Button-div-add-perdiem'>
                        {isAccessGranted([PRIVILEGES.ADD_PERDIEMS]) && <Button variant='contained' size='small' color='secondary' className='Add-perdiem-button'
                            disabled={!(travel.status === travelStatus.INPROGRESS || travel.status === travelStatus.EXTENDED)}
                            startIcon={<AddCircleOutlineIcon />} onClick={validateAndAddPerdiem}>Add Per Diem</Button>}
                    </Grid>
                    {isAccessGranted([PRIVILEGES.ADD_PERDIEMS]) && <Grid item lg={9} className='Button-div-save-reset'>
                        <Button variant='contained' size='small' color='secondary' className='Save-button'
                            disabled={!isSaveEnable || perdiems.length === 0}
                            onClick={savePerdiems}>Save</Button>
                        <Button variant='outlined' size='small' color='primary' className='Reset-button'
                            disabled={!isResetEnable}
                            onClick={resetPerdiems}>Reset</Button>
                    </Grid>}

                </Grid>
            </div>
        </div>
    );
}

export default PerdiemInformation;
